import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { useBotsOptional } from '@/contexts/BotsContext';

export const PerformanceChart: React.FC = () => {
  const botsCtx = useBotsOptional();
  const bots = botsCtx?.bots ?? [];
  const practiceBots = botsCtx?.practiceBots ?? [];
  const backtestResults = botsCtx?.backtestResults ?? [];
  
  console.log('[PERFORMANCE CHART] ctx is', botsCtx ? 'present' : 'missing');
  
  // If context is not available, show a skeleton or return null
  if (!botsCtx) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Performance Overview</h2>
        </div>
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }
  
  // Generate real performance data from bots and backtest results
  const generatePerformanceData = () => {
    const data = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    
    // Generate data for last 7 months
    for (let i = 6; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      const monthName = months[monthIndex];
      
      // Calculate real profit from bots for this month (simplified)
      const monthlyProfit = bots?.reduce((sum, bot) => sum + (bot.profit || 0), 0) || 0;
      const totalValue = 1000 + (monthlyProfit * 10); // Base value + profit
      
      data.push({
        date: monthName,
        profit: monthlyProfit / 7, // Distribute across months
        totalValue: Math.max(1000, totalValue)
      });
    }
    
    return data;
  };
  
  const performanceData = generatePerformanceData();
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Performance Overview</h2>
        <div className="flex space-x-2">
          <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-500 rounded-md">1W</button>
          <button className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md">1M</button>
          <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-500 rounded-md">3M</button>
          <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-500 rounded-md">1Y</button>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={performanceData}>
            <defs>
              <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="date" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip />
            <Area 
              type="monotone" 
              dataKey="totalValue" 
              stroke="#3B82F6" 
              fillOpacity={1} 
              fill="url(#colorProfit)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};